import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'img'],
  attributes: {
    ...defaultSchema.attributes,
    img: ['src', 'alt', 'loading', 'className'],
  },
};

const rewriteS3Url = (url: string): string =>
  url.startsWith('s3://') ? url.replace(/^s3:\/\//, '/api/media/') : url;

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => (
  <div className={className}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
      components={{
        h1: ({ children }) => (
          <h1 className="text-3xl font-bold mb-4 mt-6 text-gray-900 dark:text-dark-text">{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-2xl font-semibold mb-3 mt-5 text-gray-900 dark:text-dark-text">{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-xl font-medium mb-2 mt-4 text-gray-900 dark:text-dark-text">{children}</h3>
        ),
        h4: ({ children }) => (
          <h4 className="text-lg font-semibold mt-3 mb-2 text-gray-900 dark:text-dark-text">{children}</h4>
        ),
        h5: ({ children }) => (
          <h5 className="text-base font-semibold mt-2 mb-2 text-gray-900 dark:text-dark-text">{children}</h5>
        ),
        h6: ({ children }) => (
          <h6 className="text-sm font-semibold mt-2 mb-1 text-gray-900 dark:text-dark-text">{children}</h6>
        ),
        p: ({ children }) => <p className="mb-1">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        a: ({ href, children }) => (
          <a
            href={href}
            className="text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        ),
        code: ({ children, className: codeClassName }) => {
          if (codeClassName?.startsWith('language-')) {
            return <code className={codeClassName}>{children}</code>;
          }
          return (
            <code className="bg-gray-100 dark:bg-dark-background-tertiary px-1 py-0.5 rounded text-sm">
              {children}
            </code>
          );
        },
        pre: ({ children }) => (
          <pre className="bg-gray-100 dark:bg-dark-background-tertiary p-3 rounded my-2 overflow-x-auto">
            {children}
          </pre>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-gray-300 dark:border-border-dark pl-4 my-2 italic text-gray-600 dark:text-dark-text-secondary">
            {children}
          </blockquote>
        ),
        ul: ({ children }) => <ul className="list-disc ml-4 my-1">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal ml-4 my-1">{children}</ol>,
        li: ({ children }) => <li className="ml-2">{children}</li>,
        table: ({ children }) => (
          <div className="overflow-x-auto scrollbar-thin my-4">
            <table className="min-w-full border-collapse border border-gray-300 dark:border-border-dark">
              {children}
            </table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="bg-gray-50 dark:bg-dark-background-tertiary">{children}</thead>
        ),
        th: ({ children }) => (
          <th className="border border-gray-300 dark:border-border-dark px-4 py-2 text-left font-semibold text-gray-900 dark:text-dark-text">
            {children}
          </th>
        ),
        tr: ({ children }) => (
          <tr className="hover:bg-gray-50 dark:hover:bg-dark-background-hover">{children}</tr>
        ),
        td: ({ children }) => (
          <td className="border border-gray-300 dark:border-border-dark px-4 py-2 text-gray-900 dark:text-dark-text">
            {children}
          </td>
        ),
        hr: () => <hr className="my-4 border-gray-300 dark:border-border-dark" />,
        img: ({ src, alt }) => (
          <img
            src={rewriteS3Url(src ?? '')}
            alt={alt ?? ''}
            className="max-w-full rounded my-2"
            loading="lazy"
          />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
);
